/**
 * TOTP nach RFC 6238 - damit auch Konten mit Zwei-Faktor im Team nutzbar bleiben.
 * Der Seed selbst liegt verschluesselt im Eintrag; gerechnet wird nur im Browser.
 */

const BASE32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32Decode(input: string): Uint8Array<ArrayBuffer> {
  const clean = input.toUpperCase().replace(/[\s=-]/g, '');
  let bits = 0;
  let value = 0;
  const out: number[] = [];

  for (const char of clean) {
    const index = BASE32.indexOf(char);
    if (index < 0) throw new Error('Der 2FA-Schlüssel enthält ungültige Zeichen.');
    value = (value << 5) | index;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((value >>> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
}

export interface TotpResult {
  code: string;
  secondsLeft: number;
}

/** `at` ist die Unixzeit in Sekunden - nur fuer Tests interessant. */
export async function totp(
  secret: string,
  step = 30,
  digits = 6,
  at?: number,
): Promise<TotpResult> {
  const keyBytes = base32Decode(secret);
  if (keyBytes.length === 0) throw new Error('Der 2FA-Schlüssel ist leer.');

  const seconds = at ?? Math.floor(Date.now() / 1000);
  const counter = Math.floor(seconds / step);

  const message = new ArrayBuffer(8);
  const view = new DataView(message);
  view.setUint32(0, Math.floor(counter / 2 ** 32));
  view.setUint32(4, counter >>> 0);

  const key = await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'HMAC', hash: 'SHA-1' },
    false,
    ['sign'],
  );
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, message));

  const offset = mac[mac.length - 1]! & 0x0f;
  const binary =
    ((mac[offset]! & 0x7f) << 24) |
    ((mac[offset + 1]! & 0xff) << 16) |
    ((mac[offset + 2]! & 0xff) << 8) |
    (mac[offset + 3]! & 0xff);

  return {
    code: String(binary % 10 ** digits).padStart(digits, '0'),
    secondsLeft: step - (seconds % step),
  };
}
